import { filterClasses } from "@swivl-mono/helpers";
import { ReactNode, forwardRef, useId } from "react";

import { ErrorText } from "../FormField/FormField.ErrorText";
import { HelperText } from "../FormField/FormField.HelperText";
import { getFormFieldWidthClass } from "../FormField/FormField.Helpers";
import { Label } from "../FormField/FormField.Label";
import { FormFieldWidth } from "../FormField/FormField.Types";
import { InputField, InputFieldProps } from "./Input.Field";
import { InputPrefix } from "./Input.Prefix";
import { InputSuffix } from "./Input.Suffix";

export enum InputSize {
  SMALL = "sm",
  MEDIUM = "md"
}

export enum LabelPosition {
  TOP = "top",
  LEFT = "left"
}

export enum InputVariant {
  OUTLINED = "outlined",
  UNDERLINED = "underlined"
}

export interface InputProps extends Omit<InputFieldProps, "error" | "hasPrefix" | "hasSuffix" | "prefix" | "inputSize" | "variant"> {
  label?: string;
  helperText?: string;
  error?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
  onSuffixClick?: () => void;
  expandable?: boolean; // for search input
  inputSize?: InputSize;
  labelPosition?: LabelPosition;
  disabled?: boolean;
  variant?: InputVariant;
  containerClassName?: string;
  width?: FormFieldWidth;
  isThemed?: boolean;
  required?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      helperText,
      error,
      prefix,
      disabled,
      expandable,
      suffix,
      onSuffixClick,
      inputSize = InputSize.MEDIUM,
      labelPosition = LabelPosition.TOP,
      variant = InputVariant.OUTLINED,
      containerClassName,
      width = FormFieldWidth.FULL,
      isThemed = false,
      required,
      id,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;
    const containerWidthClass = getFormFieldWidthClass(width);
    const resolvedContainerClassName = filterClasses(containerWidthClass, containerClassName);

    const InputContent = (
      <div className={filterClasses("w-full", prefix ? "flex" : "relative")}>
        {prefix && (
          <InputPrefix inputSize={inputSize} error={!!error} disabled={disabled}>
            {prefix}
          </InputPrefix>
        )}
        <div className={filterClasses(prefix ? "relative flex-1" : "")}>
          <InputField
            ref={ref}
            id={inputId}
            error={!!error}
            hasPrefix={!!prefix}
            hasSuffix={!!suffix}
            disabled={disabled}
            inputSize={inputSize}
            variant={variant}
            isThemed={isThemed}
            {...props}
          />
          {suffix && (
            <InputSuffix inputSize={inputSize} disabled={disabled} onClick={onSuffixClick}>
              {suffix}
            </InputSuffix>
          )}
        </div>
      </div>
    );

    if (!label) {
      return (
        <div className={resolvedContainerClassName}>
          {InputContent}
          {error && <ErrorText>{error}</ErrorText>}
          {!error && helperText && <HelperText>{helperText}</HelperText>}
        </div>
      );
    }

    if (labelPosition === "left") {
      return (
        <div className={filterClasses("flex items-center gap-4", resolvedContainerClassName)}>
          <Label htmlFor={inputId} required={required}>
            {label}
          </Label>
          <div className="flex-1">
            {InputContent}
            {error && <ErrorText>{error}</ErrorText>}
            {!error && helperText && <HelperText>{helperText}</HelperText>}
          </div>
        </div>
      );
    }

    return (
      <div className={resolvedContainerClassName}>
        <Label htmlFor={inputId} required={required} className="mb-1">
          {label}
        </Label>
        {InputContent}
        {error && <ErrorText>{error}</ErrorText>}
        {!error && helperText && <HelperText>{helperText}</HelperText>}
      </div>
    );
  }
);

Input.displayName = "Input";
